import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import AnimatedSection from './AnimatedSection';
import { useHomepageData } from '../../hooks/useHomepageData';
import { getIconComponent } from '../../config/homepageConfig';

const FeaturesSection: React.FC = () => {
  const { data, loading } = useHomepageData();
  const features = data.features;

  return (
    <section id="features" className="py-16 bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 relative overflow-hidden">
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          {/* 增强的标题区域 */}
          <div className="inline-flex items-center px-4 py-2 bg-white/60 backdrop-blur-sm border border-gray-200/50 rounded-full text-sm font-medium mb-6 shadow-sm">
            <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
            <span className="text-gray-600">平台核心功能</span>
          </div>
          
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            <span className="text-gray-900">全方位深度分析</span>
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600">
              从新闻热点到深度思考
            </span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            通过热点新闻案例学习分析方法，掌握理性思考技巧，提升判断力和洞察力
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <div key={index} className="group relative bg-white/80 backdrop-blur-sm p-6 rounded-xl shadow-md animate-pulse border border-gray-200/50">
                <div className="w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl mb-4"></div>
                <div className="h-6 bg-gray-200 rounded-lg mb-2"></div>
                <div className="h-4 bg-gray-200 rounded mb-1"></div>
                <div className="h-4 bg-gray-200 rounded mb-4 w-4/5"></div>
                <div className="h-4 bg-gray-200 rounded w-20"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = getIconComponent(feature.icon);
              return (
                <AnimatedSection
                  key={index}
                  animation="scale-in"
                  delay={index * 100}
                >
                  {feature.comingSoon ? (
                    <div className="group relative bg-white/60 backdrop-blur-sm p-6 rounded-xl shadow-md border border-gray-200/50 overflow-hidden h-full flex flex-col opacity-80">
                      {/* 卡片背景 - 敬请期待状态 */}
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-50/50 via-gray-100/30 to-gray-50/50 rounded-2xl"></div>
                      
                      <div className="relative z-10">
                        {/* 敬请期待标签 */}
                        <div className="absolute -top-2 -right-2 bg-gray-400 text-white text-xs px-2 py-1 rounded-full font-medium">
                          敬请期待
                        </div>
                        
                        {/* 图标区域 */}
                        <div className="flex items-center mb-4">
                          <div className="relative w-12 h-12 bg-gradient-to-br from-gray-100 via-gray-100 to-gray-200 rounded-xl flex items-center justify-center mr-3">
                            <Icon className="w-6 h-6 text-gray-400" />
                          </div>
                        </div>
                        
                        {/* 标题 */}
                        <h3 className="text-xl font-bold text-gray-500 mb-3">
                          {feature.title}
                        </h3>
                        
                        {/* 描述 */}
                        <p className="text-gray-400 mb-4 leading-relaxed flex-grow">
                          {feature.description}
                        </p>
                        
                        {/* 敬请期待按钮 */}
                        <div className="inline-flex items-center px-3 py-2 bg-gray-300 text-gray-500 text-sm font-medium rounded-lg cursor-not-allowed mt-auto">
                          <span>敬请期待</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={feature.href}
                      className="group relative bg-white/80 backdrop-blur-sm p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-500 border border-gray-200/50 hover:border-blue-200/50 overflow-hidden block h-full flex flex-col"
                    >
                      {/* 卡片背景光效 */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/0 via-purple-50/0 to-indigo-50/0 group-hover:from-blue-50/50 group-hover:via-purple-50/30 group-hover:to-indigo-50/50 transition-all duration-500 rounded-2xl"></div>
                      
                      {/* 悬浮光点 */}
                      <div className="absolute top-4 right-4 w-2 h-2 bg-blue-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>
                      
                      <div className="relative z-10">
                        {feature.isNew && (
                          <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium animate-pulse">
                            新功能
                          </div>
                        )}
                        
                        {/* 图标区域 */}
                        <div className="flex items-center mb-4">
                          <div className="relative w-12 h-12 bg-gradient-to-br from-blue-100 via-purple-100 to-indigo-100 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-300">
                            <Icon className="w-6 h-6 text-blue-600 group-hover:text-purple-600 transition-colors duration-300" />
                            {/* 图标光环 */}
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
                          </div>
                        </div>
                        
                        {/* 标题 */}
                        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-900 transition-colors duration-300">
                          {feature.title}
                        </h3>
                        
                        {/* 描述 */}
                        <p className="text-gray-600 mb-4 leading-relaxed group-hover:text-gray-700 transition-colors duration-300 flex-grow">
                          {feature.description}
                        </p>
                        
                        {/* 了解更多按钮 */}
                        <div className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 group-hover:shadow-lg group-hover:scale-105 mt-auto">
                          <span>了解更多</span>
                          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                        </div>
                      </div>
                      
                      {/* 底部装饰线 */}
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                    </Link>
                  )}
                </AnimatedSection>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturesSection;